/**
 * monoIconButton — the one definition of a square icon control.
 *
 * Before this existed the same ~30-class string was hand-copied into eleven
 * places across seven files (Layout, PlanningLayout, Sidebar, PlanningSidebar,
 * ThemeToggle, NotificationBell, HarryHeaderActions). Copies drift: the
 * notification bell's had `border-gray-200` instead of `border-transparent`,
 * so one icon in the top rail carried a permanent box while its neighbours
 * only showed one on hover. Nobody chose that — it was a copy that aged.
 *
 * Rules encoded here so no call site has to remember them:
 *  - SQUARE. The product surface has no rounded corners.
 *  - Tokens only. No `gray-*`, no `dark:` colour variants — one class string
 *    serves both themes because the tokens flip underneath it.
 *  - The border is transparent until hover, so the control has a hit target
 *    without painting a box at rest.
 *
 * Size: 'sm' (h-8) for dense rails, 'md' (h-9) to sit level with buttons and
 * selects, which share that height.
 *
 * `active` is for toggles that stay pressed (an open panel). It lives here
 * rather than at the call site because a bespoke active state is exactly how
 * the previous eleven copies began diverging.
 */
export function monoIconButton(
  size: 'sm' | 'md' = 'md',
  extra = '',
  active = false,
): string {
  const box = size === 'sm' ? 'h-8 w-8' : 'h-9 w-9';
  return [
    'inline-flex items-center justify-center p-0 border transition-colors',
    box,
    active
      ? 'border-[var(--slate)] bg-[var(--active-tint)] text-[var(--text-pri)]'
      : 'border-transparent text-[var(--text-ter)] hover:text-[var(--text-pri)] hover:bg-[var(--hover-tint)] hover:border-[var(--rule-bright)]',
    extra,
  ]
    .filter(Boolean)
    .join(' ');
}
